/**
 * Client-side helpers for OpenRouter video generation.
 *
 * Data model:
 *   videoProjects/{projectId} -> user-owned project/workspace
 *   videoJobs/{jobId}        -> backend-owned generation job linked to a project
 */

import { authHeaders } from "./authHeaders";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  Unsubscribe,
} from "firebase/firestore";
import { db } from "./config";

export type VideoStatus =
  | "pending"
  | "in_progress"
  | "completed"
  | "failed"
  | "expired"
  | "cancelled";

export type VideoGenerationMode =
  | "text_to_video"
  | "frame_to_video"
  | "reference_to_video";

export type VideoFrameType = "first_frame" | "last_frame";
export type ProviderParameterValue = string | number | boolean | null;

export interface VideoFrameImageInput {
  frameType: VideoFrameType;
  url: string;
}

export interface VideoReferenceImageInput {
  url: string;
}

export interface VideoJob {
  uid: string;
  projectId: string | null;
  sceneTitle: string | null;
  sceneIndex: number | null;
  model: string;
  modelName?: string | null;
  prompt: string;
  generationMode?: VideoGenerationMode;
  status: VideoStatus;
  progress: number | null;
  resolution: string | null;
  aspectRatio: string | null;
  size?: string | null;
  duration: number | null;
  generateAudio: boolean;
  seed?: number | null;
  frameImageUrls?: Array<{ frameType: VideoFrameType; url: string }>;
  inputReferenceUrls?: string[];
  providerSlug?: string | null;
  providerParameters?: Record<string, ProviderParameterValue>;
  inputImageUrl?: string | null;
  openRouterJobId: string;
  generationId?: string | null;
  pollingUrl?: string | null;
  videoUrl: string | null;
  fileName: string | null;
  mimeType: string | null;
  error: string | null;
  cost: number | null;
  estimatedCost: number | null;
  estimatedCostWithMarkup?: number | null;
  freeAccess?: boolean;
  usage?: unknown;
  createdAt: any;
  updatedAt: any;
}

export interface VideoProject {
  uid: string;
  title: string;
  description?: string | null;
  totalJobs?: number;
  completedJobs?: number;
  exportStatus?: "exporting" | "completed" | "failed" | null;
  exportedVideoUrl?: string | null;
  exportedFileName?: string | null;
  exportWarning?: string | null;
  exportError?: string | null;
  createdAt: any;
  updatedAt: any;
  lastJobAt?: any;
  exportedAt?: any;
}

export interface VideoModelMeta {
  id: string;
  canonical_slug?: string | null;
  name: string;
  description?: string;
  supported_resolutions?: string[] | null;
  supported_aspect_ratios?: string[] | null;
  supported_sizes?: string[] | null;
  supported_durations?: number[] | null;
  supported_frame_images?: VideoFrameType[] | null;
  pricing_skus?: Record<string, string>;
  allowed_passthrough_parameters?: string[] | null;
  generate_audio?: boolean | null;
  seed?: boolean | null;
  [key: string]: unknown;
}

export interface SubmitVideoParams {
  uid: string;
  email: string;
  projectId: string | null;
  sceneTitle?: string | null;
  sceneIndex?: number | null;
  model: string;
  prompt: string;
  generationMode: VideoGenerationMode;
  duration?: number;
  resolution?: string | null;
  aspectRatio?: string | null;
  size?: string | null;
  generateAudio?: boolean;
  seed?: number | null;
  frameImages?: VideoFrameImageInput[];
  inputReferences?: VideoReferenceImageInput[];
  providerParameters?: Record<string, ProviderParameterValue>;
}

export interface EstimateCostParams {
  model: string;
  generationMode: VideoGenerationMode;
  duration?: number;
  resolution?: string | null;
  aspectRatio?: string | null;
  size?: string | null;
  generateAudio?: boolean;
  frameImages?: VideoFrameImageInput[];
  inputReferences?: VideoReferenceImageInput[];
}

const VIDEO_MODELS_URL =
  "https://getvideomodels-mt62q3wpaa-uc.a.run.app/api/video/models";
const VIDEO_SUBMIT_URL =
  "https://submitvideogeneration-mt62q3wpaa-uc.a.run.app/api/video/submit";
const VIDEO_POLL_URL =
  "https://pollvideostatus-mt62q3wpaa-uc.a.run.app/api/video/poll";
const VIDEO_ESTIMATE_URL =
  "https://estimatevideocostendpoint-mt62q3wpaa-uc.a.run.app/api/video/estimate";
const VIDEO_EXPORT_URL =
  "https://us-central1-familyaichat-1998.cloudfunctions.net/exportVideoProject";
const VIDEO_ASSIGN_URL =
  "https://us-central1-familyaichat-1998.cloudfunctions.net/assignVideoJobsToProject";
const VIDEO_DELETE_PROJECT_URL =
  "https://us-central1-familyaichat-1998.cloudfunctions.net/deleteVideoProject";

let modelCacheData: VideoModelMeta[] | null = null;
let modelCacheFetchedAt = 0;
const MODEL_CACHE_TTL_MS = 5 * 60 * 1000;

export async function fetchVideoModels(): Promise<VideoModelMeta[]> {
  if (modelCacheData && Date.now() - modelCacheFetchedAt < MODEL_CACHE_TTL_MS) {
    return modelCacheData;
  }
  const resp = await fetch(VIDEO_MODELS_URL);
  if (!resp.ok) throw new Error("Failed to fetch video models");
  const json = await resp.json();
  modelCacheData = json.data ?? [];
  modelCacheFetchedAt = Date.now();
  return modelCacheData!;
}

export async function submitVideo(
  params: SubmitVideoParams,
): Promise<{ jobId: string; openRouterJobId: string; estimatedCost: number; projectId: string | null }> {
  const resp = await fetch(VIDEO_SUBMIT_URL, {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify(params),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.error || `Submit failed (${resp.status})`);
  }
  return resp.json();
}

export async function pollVideo(
  jobId: string,
  uid: string,
): Promise<{ status: string; videoUrl?: string; error?: string }> {
  const resp = await fetch(VIDEO_POLL_URL, {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify({ jobId, uid }),
  });
  if (!resp.ok) {
    throw new Error(`Poll failed (${resp.status})`);
  }
  return resp.json();
}

function timestampMillis(value: any): number {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value === "number") return value;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

export async function estimateCost(
  params: EstimateCostParams,
): Promise<{ estimatedCost: number; estimatedCostWithMarkup: number }> {
  const resp = await fetch(VIDEO_ESTIMATE_URL, {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify(params),
  });
  if (!resp.ok) throw new Error("Failed to estimate cost");
  return resp.json();
}

export async function exportProjectVideo(params: {
  uid: string;
  projectId: string;
  clipIds?: string[];
}): Promise<{
  status: "completed";
  videoUrl: string;
  fileName: string;
  warning?: string | null;
}> {
  const resp = await fetch(VIDEO_EXPORT_URL, {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify(params),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.error || `Export failed (${resp.status})`);
  }
  return resp.json();
}

export async function assignVideoJobsToProject(params: {
  uid: string;
  projectId: string | null;
  jobIds: string[];
}): Promise<{
  status: "completed";
  moved: number;
  projectId: string | null;
}> {
  const resp = await fetch(VIDEO_ASSIGN_URL, {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify(params),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.error || `Move failed (${resp.status})`);
  }
  return resp.json();
}

export async function deleteVideoProject(params: {
  uid: string;
  projectId: string;
}): Promise<{
  status: "completed";
  projectId: string;
  unassigned: number;
}> {
  const resp = await fetch(VIDEO_DELETE_PROJECT_URL, {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify(params),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.error || `Delete failed (${resp.status})`);
  }
  return resp.json();
}

export async function createVideoProject(
  uid: string,
  title = "Untitled video project",
): Promise<string> {
  const ref = await addDoc(collection(db, "videoProjects"), {
    uid,
    title,
    description: "",
    totalJobs: 0,
    completedJobs: 0,
    exportStatus: null,
    exportedVideoUrl: null,
    exportedFileName: null,
    exportWarning: null,
    exportError: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateVideoProject(
  projectId: string,
  patch: Partial<Pick<VideoProject, "title" | "description">>,
): Promise<void> {
  await updateDoc(doc(db, "videoProjects", projectId), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

export function onUserVideoProjectsSnapshot(
  uid: string,
  callback: (projects: Array<VideoProject & { id: string }>) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const q = query(collection(db, "videoProjects"), where("uid", "==", uid));
  return onSnapshot(
    q,
    (snap) => {
      callback(
        snap.docs
          .map((d) => ({
            id: d.id,
            ...(d.data() as VideoProject),
          }))
          .sort(
            (a, b) =>
              timestampMillis(b.updatedAt ?? b.createdAt) -
              timestampMillis(a.updatedAt ?? a.createdAt),
          ),
      );
    },
    (err) => {
      console.error("onUserVideoProjectsSnapshot error:", err);
      onError?.(err);
    },
  );
}

export function onVideoJobSnapshot(
  jobId: string,
  callback: (job: VideoJob | null) => void,
): Unsubscribe {
  const ref = doc(db, "videoJobs", jobId);
  return onSnapshot(ref, (snap) => {
    callback(snap.exists() ? (snap.data() as VideoJob) : null);
  });
}

export function onUserVideoJobsSnapshot(
  uid: string,
  callback: (jobs: Array<VideoJob & { id: string }>) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const q = query(collection(db, "videoJobs"), where("uid", "==", uid));
  return onSnapshot(
    q,
    (snap) => {
      const jobs = snap.docs
        .map((d) => ({
          id: d.id,
          ...(d.data() as VideoJob),
        }))
        .sort(
          (a, b) => timestampMillis(b.createdAt) - timestampMillis(a.createdAt),
        );
      callback(jobs);
    },
    (err) => {
      console.error("onUserVideoJobsSnapshot error:", err);
      onError?.(err);
    },
  );
}

export async function isVideoAllowlisted(email: string): Promise<boolean> {
  const snap = await getDoc(doc(db, "config", "videoAllowList"));
  if (!snap.exists()) return false;
  const emails: string[] = snap.data()?.emails ?? [];
  return emails.map((e) => e.toLowerCase()).includes(email.toLowerCase());
}
